import { useMemo } from "react";

import { useCMSReports } from "./useCMSData";

/**
 * CMS `route` is free text with no validation, so it arrives with or without a
 * leading slash and sometimes a trailing one. Exported because ReportTelemetry
 * must match on exactly the same rule — two near-identical matchers is how the
 * download series and the report-opened series drift apart.
 */
export const normalizeRoute = (route?: string): string => {
  const trimmed = (route ?? "").trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

/**
 * Resolves a report page's heading from the CMS `reports` collection by matching
 * the report's configured route. Falls back to `fallbackTitle` while the reports
 * index loads, or when no CMS report matches the route (e.g. the report isn't
 * part of the user's product plans).
 */
export const useReportTitle = (
  route: string,
  fallbackTitle: string,
): string => {
  const { reports } = useCMSReports();

  return useMemo(() => {
    const target = normalizeRoute(route);
    if (!target) return fallbackTitle;
    const match = reports.find((r) => normalizeRoute(r.route) === target);
    return match?.name?.trim() || fallbackTitle;
  }, [reports, route, fallbackTitle]);
};
